// A child matches the current path if it IS that path or is nested under it.
// Guarding with the trailing slash prevents '/eventsxyz' matching '/events'.
export function matchNav(pathname, childPath) {
  if (!pathname || !childPath) return false;
  return pathname === childPath || pathname.startsWith(childPath + '/');
}

export function isGroupExpanded(pathname, group) {
  return group.children.some((child) => matchNav(pathname, child.path));
}

export function defaultChildPath(group) {
  return group.children[0].path;
}

// Ordered desktop layout for staff/admin accounts.
// `iconPath` reuses the icon of the flat nav item at that path (see Sidebar.js).
//
// `section` entries are headings from the redesign, splitting the nav by where
// the work happens: the front counter, the studio bench, and the back office.
// They are labels only — they render nothing when the sidebar is collapsed to
// its icon rail, and they never affect routing or which items exist.
export const STAFF_NAV_LAYOUT = [
  { type: 'section', label: 'Counter' },
  { type: 'item', path: '/' },
  { type: 'item', path: '/create-order' },
  { type: 'item', path: '/orders' },
  { type: 'item', path: '/dispatch' },
  {
    type: 'group',
    key: 'customers',
    label: 'Customers',
    iconPath: '/customers',
    children: [
      { path: '/customers', label: 'All customers' },
      { path: '/house-accounts', label: 'House accounts' },
    ],
  },
  { type: 'item', path: '/driver' },
  { type: 'section', label: 'Studio' },
  {
    type: 'group',
    key: 'events',
    label: 'Events',
    iconPath: '/events',
    children: [
      { path: '/events', label: 'Overview' },
      { path: '/production', label: 'Production', badge: 'orderCount' },
      { path: '/install-board', label: 'Installs' },
      { path: '/proposals', label: 'Proposals' },
    ],
  },
  // Online store (Phase 1). Each child needs the paid feature; the group also
  // hides when the shop turns "Online store in the sidebar" off in Settings,
  // which shops selling through Shopify or WooCommerce do.
  {
    type: 'group',
    key: 'store',
    label: 'Online store',
    iconPath: '/store/products',
    children: [
      // Go-live checklist (Phase 8D).
      { path: '/store', label: 'Overview', entitlement: 'online_store', end: true },
      // `upsell`: a shop known not to have the feature still sees the entry,
      // and the page shows "Add to your plan" (billing B4).
      { path: '/store/products', label: 'Products', entitlement: 'online_store', upsell: true },
      { path: '/store/collections', label: 'Collections', entitlement: 'online_store', upsell: true },
      { path: '/store/addons', label: 'Add-ons', entitlement: 'online_store', upsell: true },
      // Theme editor (3C): colors, fonts, logo, home page sections and a preview.
      { path: '/store/theme', label: 'Theme', entitlement: 'online_store', upsell: true },
      // Pages and menus editors (3C).
      { path: '/store/pages', label: 'Pages', entitlement: 'online_store', upsell: true },
      { path: '/store/menus', label: 'Menus', entitlement: 'online_store', upsell: true },
      // Checkout extras (Phase 4C).
      { path: '/store/discounts', label: 'Discounts', entitlement: 'online_store', upsell: true },
      { path: '/store/gift-cards', label: 'Gift cards', entitlement: 'online_store', upsell: true },
      // Shopper accounts (5A).
      { path: '/store/customers', label: 'Customers', entitlement: 'online_store', upsell: true },
      // Custom domains (Phase 6B). `ownerOnly`: hidden from admins and staff.
      { path: '/store/domain', label: 'Domain', entitlement: 'online_store', ownerOnly: true },
      // Old-site redirects (Phase 8B). Moves under Domain when Phase 6 builds that screen.
      { path: '/store/redirects', label: 'Redirects', entitlement: 'online_store' },
      // Website Connector (Phase 7E): orders from the shop's WooCommerce site.
      { path: '/store/inbound', label: 'Inbound orders', entitlement: 'site_connectors' },
      // The zone editor lives in Settings → Delivery (every shop needs it); this is a link.
      { path: '/settings?tab=delivery', label: 'Delivery rules', entitlement: 'online_store' },
    ],
  },
  { type: 'item', path: '/formulas' },
  { type: 'section', label: 'Back office' },
  {
    type: 'group',
    key: 'insights',
    label: 'Insights',
    iconPath: '/reports',
    children: [
      { path: '/reports', label: 'Reports' },
      { path: '/issues', label: 'Issues' },
      { path: '/weekly-scan', label: 'Weekly scan' },
      { path: '/waste', label: 'Waste log' },
      { path: '/recurring-review', label: 'Recurring' },
      { path: '/industry-news', label: 'Industry news' },
    ],
  },
  // Settings belongs with the rest of the back office, as in the design. It
  // used to sit alone in the sidebar footer, below the user avatar.
  { type: 'item', path: '/settings' },
];

// Inbound orders on its own, for a shop that hides the Online store group
// (Settings → General) but takes orders from its WooCommerce site: those are
// the shops that turn the group off. Sidebar.js renders an item it has no
// flat nav entry for from `label` and the icon at `iconPath`.
export const INBOUND_ORDERS_ITEM = {
  type: 'item', path: '/store/inbound', label: 'Inbound orders', entitlement: 'site_connectors', iconPath: '/store/products',
};

// The cash register's entry (Counter section, after Orders).
export const REGISTER_ITEM = { type: 'item', path: '/register' };
let registerLayout = { from: null, length: 0, layout: null };
function withRegister(layout) {
  if (registerLayout.from === layout && registerLayout.length === layout.length) return registerLayout.layout;
  const at = layout.findIndex((e) => e.type === 'item' && e.path === '/orders');
  const next = at < 0 ? [...layout, REGISTER_ITEM] : [...layout.slice(0, at + 1), REGISTER_ITEM, ...layout.slice(at + 1)];
  registerLayout = { from: layout, length: layout.length, layout: next };
  return next;
}

/**
 * The staff layout for right now. Issues (problem reports) live in Insights; while any
 * are open they move up under Dispatch so they're hard to miss, and go back
 * once they're resolved. Bouqmark's own screens (shops, texting registrations)
 * are not here: they live in the separate Bouqmark Console app (console/).
 */
export function staffNavLayout({ openIssues = 0, entitlements, entitlementsKnown = false, onlineStoreInNav = true, owner, cashRegister = false } = {}) {
  // Register (the cash drawer) sits under Orders only when the shop's
  // cash_register_enabled setting is on; without it the layout is returned
  // by reference as before.
  let base = cashRegister === true ? withRegister(STAFF_NAV_LAYOUT) : STAFF_NAV_LAYOUT;
  // Entries and group children may carry `entitlement`: a feature key the shop
  // must hold. Until the list has loaded (undefined/null) nothing is hidden, so
  // the nav never flashes. Groups left with no children disappear too.
  // `onlineStoreInNav === false` (Settings → General) drops the store group;
  // a shop with site_connectors keeps Inbound orders as a lone item in its place.
  // Entries marked `upsell` stay when the list is known (entitlementsKnown) so
  // the page can offer the add-on; staff whose list is unknown never see them.
  // Entries marked `ownerOnly` (Domain) drop when `owner === false`.
  if (Array.isArray(entitlements) || onlineStoreInNav === false || owner === false) {
    const allowed = (e) => !(e.ownerOnly && owner === false) && (
      !e.entitlement || !Array.isArray(entitlements) || entitlements.includes(e.entitlement)
      || (entitlementsKnown && e.upsell === true));
    let changed = false;
    const filtered = [];
    for (const entry of base) {
      if (entry.type === 'group' && entry.key === 'store' && onlineStoreInNav === false) {
        changed = true;
        if (allowed(INBOUND_ORDERS_ITEM)) filtered.push(INBOUND_ORDERS_ITEM);
        continue;
      }
      if (!allowed(entry)) { changed = true; continue; }
      if (entry.type === 'group') {
        const children = entry.children.filter(allowed);
        if (children.length === 0) { changed = true; continue; }
        if (children.length !== entry.children.length) { changed = true; filtered.push({ ...entry, children }); continue; }
      }
      filtered.push(entry);
    }
    if (changed) base = filtered;
  }
  if (!(openIssues > 0)) return base;
  const layout = [];
  for (const entry of base) {
    if (entry.type === 'group' && entry.children.some((c) => c.path === '/issues')) {
      layout.push({ ...entry, children: entry.children.filter((c) => c.path !== '/issues') });
    } else {
      layout.push(entry);
    }
    if (entry.type === 'item' && entry.path === '/dispatch') layout.push({ type: 'item', path: '/issues' });
  }
  return layout;
}
